import "./errorBoundary.scss";

import { has } from "lodash";
import PropTypes from "prop-types";
import React from "react";

import Dimensions from "../../constants/styles/dimensions";
import ServerErrorLog from "../../serverErrorLog";
import { PropTypeShapes } from "../../types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { withContainerContext } from "../container/ContainerContext";
import FormButton from "../form/FormButton";
import ModalFooter from "../modal/ModalFooter";
import ModalFooterRightGroup from "../modal/ModalFooterRightGroup";
import ModalHeading from "../modal/ModalHeading";
import ModalView from "../modal/ModalView";

/* eslint-disable no-console */

// ErrorBoundary allows us to capture React rendering errors, and:
// 1) Show an error message to the user
// 2) Limit their impact to a portion of the screen
// 3) Log the error somewhere so we can track it in the future
// 4) Gracefully recover from the error and continue app usage

// In order to achieve this last point, we need a way to reset the error so that it is
// not locked into its error state forever (e.g. if the user changes state in such a way
// the error is resolved)

// There are three methods provided for this:
// 1) Pass a "resetValue" prop (type: any)
// 2) Pass a "resetKey" prop (type: array of strings):
//      - we will use this key to extract a "resetValue" from the redux store
//        (specifically from state.present)
// 3) Pass neither "resetKey" or "resetValue" (default "global" case)
//      - we will use state.present as the reset value (i.e. any redux state change will trigger it)
//
// Once the Component has a "resetValue" prop through whatever means it arrives, it will compare the
// old and new values, and reset the state whenever the resetValue changes (using === comparison)
//
// So in the default "global" case, every time the redux store changes it will attempt to reset the error
//
// It will be slightly more efficient to pass a more limiting resetKey into the Component, but in order to maximise
// the chance of a successful recovery, the key used should cover the entire state tree that is touched by any
// of the child components (otherwise, the value causing the error may change, but the app still won't recover)
//
// For this reason, we should move away from the global default judiciously.
//
// For reference, passing in the first "resetValue" may be suitable for components such as Modal windows that
// maintain their own internal state, where we might want to use those state variables instead of the true
// redux store to control the resetting.

const isIos = preprocessorQuery.isIos();

const resetValueFromProps = props => {
  if (has(props, "resetValue")) {
    return props.resetValue;
  }
  if (props.store) {
    if (props.resetKey) {
      return props.store.getIn(props.resetKey);
    }
    return props.store;
  }
  return null;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, hasLoggedError: false, isModalOpen: false };
  }

  componentWillReceiveProps(nextProps) {
    // If the resetValue has changed since last time, reset the hasError flag to allow
    // it to render again if something improves.
    // We do not reset hasLoggedError though, so that it doesn't continuously spam Sentry and
    // use up our quota because we continually reset this component.
    if (resetValueFromProps(this.props) !== resetValueFromProps(nextProps)) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, info) {
    // Display fallback UI
    this.setState({ hasError: true });

    if (this.props.isModal) {
      this.setState({ isModalOpen: true });
    }

    // Console Log the error
    console.log(`ErrorBoundary.componentDidCatch(): printing error, info`);
    console.dir(error);
    console.dir(info);

    // And now log the error on the server.
    // We only do this once per error-component mounting, so that we do not continuously spam Sentry
    // with the same entry and use up our quota just because we continually reset this component.
    // It is not perfect, since they may unmount and remount it, and it will log again, but it is
    // better than letting everything through always.
    if (!this.state.hasLoggedError) {
      // Log the error to the server: currently, this wraps Sentry but does not send errors in dev
      // environment.
      ServerErrorLog.sendError(error);
    }
  }

  getModalErrorComponent(content) {
    const modalTitle = this.props.modalTitle || "Modal Error";
    const fixedHeight = Dimensions.MODAL_MAX_HEIGHT;
    const onClose = () => {
      this.setState({ isModalOpen: false });
      this.props.onCloseModal();
    };
    const modalHeading = isIos ? (
      <ModalHeading title={modalTitle} showBack={true} onBackClick={onClose} />
    ) : (
      <ModalHeading title={modalTitle} showCancel={true} onCancelClick={onClose} />
    );

    const modalFooter = isIos ? null : (
      <ModalFooter>
        <ModalFooterRightGroup>
          <FormButton primary={true} label="Close" onClick={onClose} />
        </ModalFooterRightGroup>
      </ModalFooter>
    );

    return this.state.isModalOpen ? (
      <ModalView header={modalHeading} fixedHeight={fixedHeight} footer={modalFooter}>
        {content}
      </ModalView>
    ) : null;
  }

  renderError() {
    // TODO: We may want to add better styling, and more options to suit a wider range of locations
    // in the UI.
    const style = this.props.containerRect
      ? {
        width: `${this.props.containerRect.width}px`,
        height: `${this.props.containerRect.height}px`,
        position: this.props.absolute ? "absolute" : this.props.fixed ? "fixed" : "static"
      }
      : {};

    const content = (
      <div className="errorBoundaryErrorMessage" style={style}>
        <div className="errorBoundaryErrorMessageContent">
          <h1>Something has gone wrong</h1>
          <p>
            An error has occurred, and we cannot display this view. We have logged this error and will fix it as soon as
            we can.
          </p>
          <p>In the mean time, you can continue working in other views, or try the following:</p>
          <ul>
            <li>Undo your previous action</li>
            <li>Closing and reopening this file</li>
            <li>Contact support if the problem persists</li>
          </ul>
        </div>
      </div>
    );

    return this.props.isModal ? this.getModalErrorComponent(content) : content;
  }

  render() {
    if (this.state.hasError) {
      // Render error
      return this.renderError();
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypeShapes.elementOrArray,
  absolute: PropTypes.bool,
  fixed: PropTypes.bool,
  isModal: PropTypes.bool,
  modalTitle: PropTypes.string,
  onCloseModal: PropTypes.func,

  // Resetting options
  store: PropTypes.any, // eslint-disable-line
  resetValue: PropTypes.any, // eslint-disable-line
  resetKey: PropTypes.array,

  // From containerContext
  containerRect: PropTypeShapes.flatRect
};

export default withContainerContext(ErrorBoundary);
